import { create } from 'zustand';
import { persist } from 'zustand/middleware'

export const useAudioPlayerStore = create((set) => ({
    audioUrl: null,
    isPlaying: false,
    from: null,
    setAudioUrl: (url) => set({ audioUrl: url, isPlaying: false }),
    setPlay: () => set({ isPlaying: true }),
    setPause: () => set({ isPlaying: false }),
    setFrom: (from) => set({ from }),
    setFeaturedImage: (url) => set({ featuredImage: url }),
    featuredImage: null,
    episodeId: null,
    setEpisodeId: (id) => set({ episodeId: id }),
    seekTo: (seconds) => set({ pendingSeek: seconds }),
    pendingSeek: null,
    clearPendingSeek: () => set({ pendingSeek: null }),
    currentTime: 0,
    duration: 0,
    setCurrentTime: (t) => set({ currentTime: t }),
    setDuration: (d) => set({ duration: d }),
}))

// ---------------------------------------------------------------------------
// Chat store — manages 1-to-1 chat threads for PodcastClubChat
//
// Layout model:
//   - chats: list of thread summaries (for the podcaster's sidebar)
//   - activeChatGuid: which thread is open
//   - messages / hasMore / oldestMessageId: messages for the active thread
// ---------------------------------------------------------------------------
export const useChatStore = create((set, get) => ({
    // Thread list (podcaster sidebar)
    chats: [],
    setChats: (chats) => set({ chats }),
    prependChat: (chat) => set((state) => ({ chats: [chat, ...state.chats.filter(c => c.chat_guid !== chat.chat_guid)] })),
    updateChatLatestMessage: (chatGuid, latestMessage) => set((state) => ({
        chats: state.chats.map(c =>
            c.chat_guid === chatGuid ? { ...c, latest_message: latestMessage, updated_at: latestMessage.created_at } : c
        ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
    })),

    // Active thread
    activeChatGuid: null,
    setActiveChatGuid: (guid) => set({ activeChatGuid: guid }),

    // Messages for the active thread
    messages: [],
    isLoading: false,
    hasMore: true,
    oldestMessageId: null,

    setLoading: (isLoading) => set({ isLoading }),

    setMessages: (messages) => {
        const oldest = messages.length > 0 ? messages[0].id : null;
        set({ messages, oldestMessageId: oldest });
    },

    prependMessages: (olderMessages) => {
        const current = get().messages;
        const merged = [...olderMessages, ...current];
        const oldest = merged.length > 0 ? merged[0].id : null;
        set({ messages: merged, oldestMessageId: oldest });
    },

    appendMessage: (message) => {
        set((state) => ({ messages: [...state.messages, message] }));
    },

    setHasMore: (hasMore) => set({ hasMore }),

    // Call when switching clubs or unmounting
    reset: () => set({
        chats: [],
        activeChatGuid: null,
        messages: [],
        isLoading: false,
        hasMore: true,
        oldestMessageId: null,
    }),

    // Call when switching to a different thread (keeps chats list intact)
    resetThread: () => set({ messages: [], isLoading: false, hasMore: true, oldestMessageId: null }),
}));

export const useUiStore = create(
    persist(
        (set) => ({
            showPodcasterPodcastClubOnboardingVideo: false,
            setShowPodcasterPodcastClubOnboardingVideo: (val) => set({ showPodcasterPodcastClubOnboardingVideo: val }),
            listenerSubscribedToClub: false,
            setListenerSubscribedToClub: (val) => set({ listenerSubscribedToClub: val }),
        }),
        {
            name: 'ui-storage',
            partialize: (state) => ({
                showPodcasterPodcastClubOnboardingVideo: state.showPodcasterPodcastClubOnboardingVideo,
            }),
        }
    )
)

export const useAuthStore = create(
    persist(
        (set) => ({
            token: null,
            user: {
                first_name : '',
                avatar_url : '',
                user_id : '',
                guid: '',
                roles : [],
                is_admin: false,
            },
            setToken: (token) => set({ token }),
            clearToken: () => set({ token: null }),
            setUser: (user) => set({ user }),
            clearUser: () => set({ user: null }),
        }),
        {
            name: 'auth-storage', // key in localStorage
            partialize: (state) => ({ token: state.token, user: state.user }), // only persist token and user
        }
    )
)